import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState,
} from "react";
import type { MutableRefObject, ReactNode } from "react";
import type { Camera } from "three";
import { useToolParamPersistence } from "../../utils/toolParamPersistence";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { cloneArray } from "../../utils/undoSnapshots";
import {
    buildVolumeExportSvg,
    compilePaths,
    createDefaultPath,
    normalizePath,
    packObjects,
    projectVolumesToDrawables,
} from "../../utils/volumeBuilderGeometry";
import type {
    PackingBounds,
    Path3D,
    Vec3,
    VolumeDrawable,
    VolumeProgress,
} from "../../utils/volumeBuilderGeometry";

export interface VolumeCameraParams {
    fov: number;
    projectionType: "perspective" | "orthographic";
    distance: number;
}

type ViewMode = "preview" | "result";

interface VolumeBuilder3dState {
    // Authoring
    paths: Path3D[];
    addPath: () => void;
    removePath: (id: string) => void;
    updatePath: (id: string, partial: Partial<Path3D>) => void;
    reorderPath: (from: number, to: number) => void;
    addPoint: (pathId: string) => void;
    removePoint: (pathId: string, index: number) => void;
    updatePoint: (pathId: string, index: number, point: Vec3) => void;
    // Undo/redo
    undo: () => void;
    redo: () => void;
    canUndo: boolean;
    canRedo: boolean;
    pushPathsSnapshot: () => void;

    // Camera + rendering params
    cameraParams: VolumeCameraParams;
    setCameraParams: (partial: Partial<VolumeCameraParams>) => void;
    packingBounds: PackingBounds;
    setPackingBounds: (partial: Partial<PackingBounds>) => void;
    palette: string[];
    setPalette: (palette: string[]) => void;
    ambient: number;
    setAmbient: (v: number) => void;
    backfaceCulling: boolean;
    setBackfaceCulling: (v: boolean) => void;
    faceStrokeEnabled: boolean;
    setFaceStrokeEnabled: (v: boolean) => void;
    faceStrokeColor: string;
    setFaceStrokeColor: (v: string) => void;
    faceStrokeWidth: number;
    setFaceStrokeWidth: (v: number) => void;
    viewBoxWidth: number;
    setViewBoxWidth: (v: number) => void;
    viewBoxHeight: number;
    setViewBoxHeight: (v: number) => void;

    // Output
    viewMode: ViewMode;
    setViewMode: (mode: ViewMode) => void;
    drawables: VolumeDrawable[];
    generatedSvg: string | null;
    isGenerating: boolean;
    generateProgress: VolumeProgress | null;
    error: string | null;
    cameraRef: MutableRefObject<Camera | null>;
    generate: () => void;
    canExport: boolean;
    handleDownload: () => void;
    buildExportSvgString: () => string;

    // Persistence / presets
    getPresetValues: () => Record<string, unknown>;
    applyPreset: (values: Record<string, unknown>) => void;
    resetParameters: () => void;
}

const VolumeBuilder3dContext = createContext<VolumeBuilder3dState | null>(null);

export function useVolumeBuilder3d(): VolumeBuilder3dState {
    const ctx = useContext(VolumeBuilder3dContext);
    if (!ctx) {
        throw new Error(
            "useVolumeBuilder3d must be used within VolumeBuilder3dProvider",
        );
    }
    return ctx;
}

const DEFAULT_CAMERA: VolumeCameraParams = {
    fov: 45,
    projectionType: "perspective",
    distance: 7,
};
const DEFAULT_PACKING_BOUNDS: PackingBounds = { x: 6, y: 6, z: 6 };
const DEFAULT_PALETTE = ["#1d2b53", "#5a7fc7", "#c8ddff"];
const DEFAULT_AMBIENT = 0.3;

function makeDefaultPaths(): Path3D[] {
    return [createDefaultPath(0)];
}

function defaultPreset(): Record<string, unknown> {
    return {
        paths: makeDefaultPaths(),
        cameraFov: DEFAULT_CAMERA.fov,
        cameraProjectionType: DEFAULT_CAMERA.projectionType,
        cameraDistance: DEFAULT_CAMERA.distance,
        packingBoundsX: DEFAULT_PACKING_BOUNDS.x,
        packingBoundsY: DEFAULT_PACKING_BOUNDS.y,
        packingBoundsZ: DEFAULT_PACKING_BOUNDS.z,
        palette: [...DEFAULT_PALETTE],
        ambient: DEFAULT_AMBIENT,
        backfaceCulling: true,
        faceStrokeEnabled: true,
        faceStrokeColor: "#0b1326",
        faceStrokeWidth: 0.5,
        viewBoxWidth: 600,
        viewBoxHeight: 600,
    };
}

export function VolumeBuilder3dProvider({ children }: { children: ReactNode }) {
    const [paths, setPaths] = useState<Path3D[]>(makeDefaultPaths);
    const pathsRef = useRef(paths);
    pathsRef.current = paths;
    const history = useUndoRedo<Path3D[]>({ clone: cloneArray });

    const pushPathsSnapshot = useCallback(() => {
        history.push(pathsRef.current);
    }, [history]);
    const undo = useCallback(() => {
        history.undo(pathsRef.current, setPaths);
    }, [history]);
    const redo = useCallback(() => {
        history.redo(pathsRef.current, setPaths);
    }, [history]);

    const [cameraParams, setCameraParamsState] =
        useState<VolumeCameraParams>(DEFAULT_CAMERA);
    const [packingBounds, setPackingBoundsState] = useState<PackingBounds>(
        DEFAULT_PACKING_BOUNDS,
    );
    const [palette, setPalette] = useState<string[]>([...DEFAULT_PALETTE]);
    const [ambient, setAmbient] = useState(DEFAULT_AMBIENT);
    const [backfaceCulling, setBackfaceCulling] = useState(true);
    const [faceStrokeEnabled, setFaceStrokeEnabled] = useState(true);
    const [faceStrokeColor, setFaceStrokeColor] = useState("#0b1326");
    const [faceStrokeWidth, setFaceStrokeWidth] = useState(0.5);
    const [viewBoxWidth, setViewBoxWidth] = useState(600);
    const [viewBoxHeight, setViewBoxHeight] = useState(600);

    const [viewMode, setViewMode] = useState<ViewMode>("preview");
    const [drawables, setDrawables] = useState<VolumeDrawable[]>([]);
    const [generatedSvg, setGeneratedSvg] = useState<string | null>(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generateProgress, setGenerateProgress] =
        useState<VolumeProgress | null>(null);
    const [error, setError] = useState<string | null>(null);

    const cameraRef = useRef<Camera | null>(null);
    const generateAbortRef = useRef<AbortController | null>(null);

    const setCameraParams = useCallback(
        (partial: Partial<VolumeCameraParams>) => {
            setCameraParamsState((prev) => ({ ...prev, ...partial }));
        },
        [],
    );
    const setPackingBounds = useCallback((partial: Partial<PackingBounds>) => {
        setPackingBoundsState((prev) => ({ ...prev, ...partial }));
    }, []);

    // --- Path CRUD (snapshot before structural edits) ---------------------

    const addPath = useCallback(() => {
        pushPathsSnapshot();
        setPaths((prev) => [...prev, createDefaultPath(prev.length)]);
    }, [pushPathsSnapshot]);

    const removePath = useCallback(
        (id: string) => {
            pushPathsSnapshot();
            setPaths((prev) => prev.filter((p) => p.id !== id));
        },
        [pushPathsSnapshot],
    );

    const updatePath = useCallback(
        (id: string, partial: Partial<Path3D>) => {
            setPaths((prev) =>
                prev.map((p) => (p.id === id ? { ...p, ...partial } : p)),
            );
        },
        [],
    );

    const reorderPath = useCallback(
        (from: number, to: number) => {
            if (from === to) return;
            pushPathsSnapshot();
            setPaths((prev) => {
                if (
                    from < 0 ||
                    from >= prev.length ||
                    to < 0 ||
                    to >= prev.length
                ) {
                    return prev;
                }
                const next = [...prev];
                const [moved] = next.splice(from, 1);
                if (moved) next.splice(to, 0, moved);
                return next;
            });
        },
        [pushPathsSnapshot],
    );

    const addPoint = useCallback(
        (pathId: string) => {
            pushPathsSnapshot();
            setPaths((prev) =>
                prev.map((p) => {
                    if (p.id !== pathId) return p;
                    const last = p.points[p.points.length - 1] ?? {
                        x: 0,
                        y: 0,
                        z: 0,
                    };
                    return {
                        ...p,
                        points: [
                            ...p.points,
                            { x: last.x, y: last.y - 1, z: last.z },
                        ],
                    };
                }),
            );
        },
        [pushPathsSnapshot],
    );

    const removePoint = useCallback(
        (pathId: string, index: number) => {
            pushPathsSnapshot();
            setPaths((prev) =>
                prev.map((p) => {
                    if (p.id !== pathId) return p;
                    if (p.points.length <= 2) return p;
                    return {
                        ...p,
                        points: p.points.filter((_, i) => i !== index),
                    };
                }),
            );
        },
        [pushPathsSnapshot],
    );

    const updatePoint = useCallback(
        (pathId: string, index: number, point: Vec3) => {
            setPaths((prev) =>
                prev.map((p) => {
                    if (p.id !== pathId) return p;
                    const points = [...p.points];
                    if (index < 0 || index >= points.length) return p;
                    points[index] = point;
                    return { ...p, points };
                }),
            );
        },
        [],
    );

    // --- Generation -------------------------------------------------------

    const generate = useCallback(() => {
        const camera = cameraRef.current;
        if (!camera) {
            setError("3D preview is not ready yet.");
            return;
        }
        generateAbortRef.current?.abort();
        const controller = new AbortController();
        generateAbortRef.current = controller;
        setIsGenerating(true);
        setError(null);
        setGenerateProgress(null);

        const objects = packObjects(
            compilePaths(pathsRef.current),
            pathsRef.current,
            packingBounds,
        );

        void projectVolumesToDrawables(objects, camera, {
            palette,
            ambient,
            backfaceCulling,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
            outputMinX: 0,
            outputMinY: 0,
            outputWidth: viewBoxWidth,
            outputHeight: viewBoxHeight,
            signal: controller.signal,
            onProgress: setGenerateProgress,
        })
            .then((result) => {
                if (controller.signal.aborted) return;
                setDrawables(result.drawables);
                const viewBoxAttr = `0 0 ${viewBoxWidth} ${viewBoxHeight}`;
                setGeneratedSvg(
                    result.drawables.length > 0
                        ? buildVolumeExportSvg(
                              result.drawables,
                              viewBoxAttr,
                              faceStrokeEnabled,
                              faceStrokeColor,
                              faceStrokeWidth,
                          )
                        : null,
                );
                if (result.drawables.length === 0) {
                    setError(
                        "Nothing projected. Check that paths are visible and within view.",
                    );
                } else {
                    setViewMode("result");
                }
            })
            .catch((e: unknown) => {
                if (controller.signal.aborted) return;
                setError(
                    e instanceof Error
                        ? `Generation failed: ${e.message}`
                        : "Generation failed.",
                );
            })
            .finally(() => {
                if (generateAbortRef.current === controller) {
                    generateAbortRef.current = null;
                }
                setIsGenerating(false);
                setGenerateProgress(null);
            });
    }, [
        packingBounds,
        palette,
        ambient,
        backfaceCulling,
        faceStrokeEnabled,
        faceStrokeColor,
        faceStrokeWidth,
        viewBoxWidth,
        viewBoxHeight,
    ]);

    const canExport = drawables.length > 0;
    const viewBoxAttr = `0 0 ${viewBoxWidth} ${viewBoxHeight}`;

    const buildExportSvgString = useCallback(() => {
        if (drawables.length === 0) return "";
        return buildVolumeExportSvg(
            drawables,
            viewBoxAttr,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
        );
    }, [
        drawables,
        viewBoxAttr,
        faceStrokeEnabled,
        faceStrokeColor,
        faceStrokeWidth,
    ]);

    const handleDownload = useCallback(() => {
        if (!canExport) return;
        const svgString = buildExportSvgString();
        if (!svgString) return;
        const blob = new Blob([svgString], {
            type: "image/svg+xml;charset=utf-8",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "volume-builder-3d.svg";
        a.click();
        URL.revokeObjectURL(url);
    }, [buildExportSvgString, canExport]);

    // --- Persistence ------------------------------------------------------

    const getPresetValues = useCallback(
        (): Record<string, unknown> => ({
            paths,
            cameraFov: cameraParams.fov,
            cameraProjectionType: cameraParams.projectionType,
            cameraDistance: cameraParams.distance,
            packingBoundsX: packingBounds.x,
            packingBoundsY: packingBounds.y,
            packingBoundsZ: packingBounds.z,
            palette,
            ambient,
            backfaceCulling,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
            viewBoxWidth,
            viewBoxHeight,
        }),
        [
            paths,
            cameraParams,
            packingBounds,
            palette,
            ambient,
            backfaceCulling,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
            viewBoxWidth,
            viewBoxHeight,
        ],
    );

    const applyPreset = useCallback((values: Record<string, unknown>) => {
        if (Array.isArray(values.paths)) {
            setPaths(
                (values.paths as Array<Partial<Path3D>>).map(normalizePath),
            );
        }
        const cam: Partial<VolumeCameraParams> = {};
        if (values.cameraFov != null) cam.fov = values.cameraFov as number;
        if (values.cameraProjectionType != null) {
            cam.projectionType =
                values.cameraProjectionType as VolumeCameraParams["projectionType"];
        }
        if (values.cameraDistance != null) {
            cam.distance = values.cameraDistance as number;
        }
        if (Object.keys(cam).length > 0) {
            setCameraParamsState((prev) => ({ ...prev, ...cam }));
        }
        const bounds: Partial<PackingBounds> = {};
        if (values.packingBoundsX != null) {
            bounds.x = values.packingBoundsX as number;
        }
        if (values.packingBoundsY != null) {
            bounds.y = values.packingBoundsY as number;
        }
        if (values.packingBoundsZ != null) {
            bounds.z = values.packingBoundsZ as number;
        }
        if (Object.keys(bounds).length > 0) {
            setPackingBoundsState((prev) => ({ ...prev, ...bounds }));
        }
        if (Array.isArray(values.palette)) {
            setPalette(values.palette as string[]);
        }
        if (values.ambient != null) setAmbient(values.ambient as number);
        if (values.backfaceCulling != null) {
            setBackfaceCulling(values.backfaceCulling as boolean);
        }
        if (values.faceStrokeEnabled != null) {
            setFaceStrokeEnabled(values.faceStrokeEnabled as boolean);
        }
        if (values.faceStrokeColor != null) {
            setFaceStrokeColor(values.faceStrokeColor as string);
        }
        if (values.faceStrokeWidth != null) {
            setFaceStrokeWidth(values.faceStrokeWidth as number);
        }
        if (values.viewBoxWidth != null) {
            setViewBoxWidth(values.viewBoxWidth as number);
        }
        if (values.viewBoxHeight != null) {
            setViewBoxHeight(values.viewBoxHeight as number);
        }
    }, []);

    const { resetParameters } = useToolParamPersistence({
        toolId: "volume-builder-3d",
        defaultPreset,
        getPresetValues,
        applyPreset,
    });

    const value = useMemo<VolumeBuilder3dState>(
        () => ({
            paths,
            addPath,
            removePath,
            updatePath,
            reorderPath,
            addPoint,
            removePoint,
            updatePoint,
            undo,
            redo,
            canUndo: history.canUndo,
            canRedo: history.canRedo,
            pushPathsSnapshot,
            cameraParams,
            setCameraParams,
            packingBounds,
            setPackingBounds,
            palette,
            setPalette,
            ambient,
            setAmbient,
            backfaceCulling,
            setBackfaceCulling,
            faceStrokeEnabled,
            setFaceStrokeEnabled,
            faceStrokeColor,
            setFaceStrokeColor,
            faceStrokeWidth,
            setFaceStrokeWidth,
            viewBoxWidth,
            setViewBoxWidth,
            viewBoxHeight,
            setViewBoxHeight,
            viewMode,
            setViewMode,
            drawables,
            generatedSvg,
            isGenerating,
            generateProgress,
            error,
            cameraRef,
            generate,
            canExport,
            handleDownload,
            buildExportSvgString,
            getPresetValues,
            applyPreset,
            resetParameters,
        }),
        [
            paths,
            addPath,
            removePath,
            updatePath,
            reorderPath,
            addPoint,
            removePoint,
            updatePoint,
            undo,
            redo,
            history.canUndo,
            history.canRedo,
            pushPathsSnapshot,
            cameraParams,
            setCameraParams,
            packingBounds,
            setPackingBounds,
            palette,
            ambient,
            backfaceCulling,
            faceStrokeEnabled,
            faceStrokeColor,
            faceStrokeWidth,
            viewBoxWidth,
            viewBoxHeight,
            viewMode,
            drawables,
            generatedSvg,
            isGenerating,
            generateProgress,
            error,
            generate,
            canExport,
            handleDownload,
            buildExportSvgString,
            getPresetValues,
            applyPreset,
            resetParameters,
        ],
    );

    return (
        <VolumeBuilder3dContext.Provider value={value}>
            {children}
        </VolumeBuilder3dContext.Provider>
    );
}
